/**
 * `DiscountCodesPanel` — the studio's Stripe discount codes, rendered for the
 * `/data` rail's **Money → Discount codes** entry.
 *
 * WHY IT IS A PANEL AND NOT A `/data` TAB. The data browser is a DATABASE
 * browser: every tab in `BROWSABLE_ENTITIES` is a real table, with cursor
 * pagination on a real ordering column, a PII-column map, and cascade-delete
 * support. A coupon is none of those things — it lives on Stripe, and Layer
 * knows it only through `metadata` it stamps (`layer_studio_id`,
 * `layer_applies_to`, `layer_member_id`). That is exactly why codes were absent
 * from `/data`: there is no `coupons` table to browse. Shoehorning a vendor read
 * into the entity browser would be a parallel path around the abstraction; a
 * panel that dispatches the canonical `list_stripe_coupons` is the sanctioned
 * route (invariant 5 — surfaces call the same action layer).
 *
 * It sits in the Money group because that is the question it answers — "what
 * discounts exist against the money coming in?".
 *
 * The owner column links rather than names. Resolving names would mean either N
 * lookups or teaching a PII-free read action to return member names, which would
 * put PII into a response that declares `responsePiiPaths: []` and therefore
 * gets nulled on idempotency replay. A link is honest and one click away.
 *
 * DELETE is a two-step confirm, and the confirm states what Stripe's delete
 * actually does. "Delete" reads like "undo", and it is not: the code stops being
 * redeemable by anyone new, but redemptions already made stand and a
 * subscription already carrying the discount keeps it. A staff member who
 * expects a refund from this button would be wrong, so the button says so.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { dispatchAction } from "../lib/dispatch";

/** One coupon as `list_stripe_coupons` returns it. */
interface CouponRow {
  readonly coupon_id: string;
  readonly name: string | null;
  readonly percent_off: number | null;
  readonly amount_off: number | null;
  readonly currency: string | null;
  readonly duration: string;
  readonly duration_in_months: number | null;
  readonly valid: boolean;
  readonly applies_to: string | null;
  /** 2026-08-14 — item scope; null = the whole class. */
  readonly applies_to_slugs: ReadonlyArray<string> | null;
  readonly max_redemptions: number | null;
  /** 2026-08-14 — per-member cap; null = unlimited. */
  readonly max_redemptions_per_member: number | null;
  readonly times_redeemed: number;
  readonly member_id: string | null;
}

/**
 * What the code may be spent on, in words. An item-scoped code names its
 * items (first few + count; the full list rides the cell's `title`), mapped
 * slug→name through the same `lookupOptions` catalogs the create form uses —
 * a slug that no longer resolves renders as itself, which is also the honest
 * display for a scope orphaned by a catalog rename.
 */
function railText(c: CouponRow, names: ReadonlyMap<string, string>): string {
  if (c.applies_to_slugs !== null && c.applies_to_slugs.length > 0) {
    const labels = c.applies_to_slugs.map((s) => names.get(s) ?? s);
    const shown = labels.slice(0, 3).join(", ");
    return labels.length > 3
      ? `${shown} +${labels.length - 3} more`
      : shown;
  }
  if (c.applies_to === "packages") return "Packages";
  if (c.applies_to !== "memberships") return "Anything";
  if (c.duration === "once") return "Memberships · 1st month";
  if (c.duration === "repeating" && c.duration_in_months !== null) {
    return `Memberships · 1st ${c.duration_in_months} months`;
  }
  return "Memberships · every month";
}

/** The full scope for the cell's `title` (hover) when it was truncated. */
function railTitle(
  c: CouponRow,
  names: ReadonlyMap<string, string>,
): string | undefined {
  if (c.applies_to_slugs === null || c.applies_to_slugs.length <= 3) {
    return undefined;
  }
  return c.applies_to_slugs.map((s) => names.get(s) ?? s).join(", ");
}

/**
 * How many spends are left. Deliberately NOT Stripe's `duration`, which answers
 * how many membership invoices ONE redemption discounts — printing it here is
 * what made a code capped at a single use read "forever". The per-member cap
 * is appended when set (2026-08-14) — it is Layer-enforced, so Stripe's
 * counter says nothing about it.
 */
function usesText(c: CouponRow): string {
  const perMember =
    c.max_redemptions_per_member !== null
      ? ` · ${c.max_redemptions_per_member}/member`
      : "";
  if (c.max_redemptions === null) {
    return `${c.times_redeemed} used · unlimited${perMember}`;
  }
  const left = Math.max(0, c.max_redemptions - c.times_redeemed);
  return `${left} of ${c.max_redemptions} left${perMember}`;
}

function discountText(c: CouponRow): string {
  if (c.percent_off !== null) return `${c.percent_off}% off`;
  if (c.amount_off !== null) {
    return `${(c.amount_off / 100).toFixed(2)} ${(c.currency ?? "").toUpperCase()} off`;
  }
  return "discount";
}

export function DiscountCodesPanel(): JSX.Element {
  const [rows, setRows] = useState<ReadonlyArray<CouponRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeFlow, setIncludeFlow] = useState(false);
  // slug → display name for item-scoped codes. Best-effort: a failed catalog
  // read leaves slugs rendering as themselves rather than blocking the list.
  const [slugNames, setSlugNames] = useState<ReadonlyMap<string, string>>(
    new Map(),
  );
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api.lookupOptions("packages"),
      api.lookupOptions("memberships"),
    ]).then(
      ([p, m]) => {
        if (cancelled) return;
        setSlugNames(
          new Map(
            [...p.options, ...m.options].map((o) => [o.value, o.label]),
          ),
        );
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, []);
  /** The row whose Delete is armed — one at a time, so a mis-click is visible. */
  const [armed, setArmed] = useState<string | null>(null);
  /** The coupon currently being deleted; locks every row's controls. */
  const [deleting, setDeleting] = useState<string | null>(null);
  /** Outcome line after a delete ("Deleted X" / "X was already gone"). */
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (withFlow: boolean) => {
    setError(null);
    const r = await dispatchAction<
      Record<string, unknown>,
      { coupons: ReadonlyArray<CouponRow> }
    >("list_stripe_coupons", {
      limit: 100,
      // The operator view: every code, whoever it belongs to. The picker's
      // default (this member's + the general ones) is the wrong shape here.
      include_member_bound: true,
      include_flow_minted: withFlow,
    });
    if (r.ok) setRows(r.response.coupons);
    else setError(`Couldn't load codes: ${r.message || r.code}`);
  }, []);

  useEffect(() => {
    void load(includeFlow);
  }, [includeFlow, load]);

  const remove = useCallback(
    async (couponId: string): Promise<void> => {
      setDeleting(couponId);
      setError(null);
      setNotice(null);
      try {
        const r = await dispatchAction<
          { coupon_id: string },
          { outcome: "deleted" | "not_found" }
        >("delete_stripe_coupon", { coupon_id: couponId });
        if (!r.ok) {
          setError(`Couldn't delete ${couponId}: ${r.message || r.code}`);
          return;
        }
        setNotice(
          r.response.outcome === "deleted"
            ? `Deleted ${couponId}.`
            : `${couponId} was already gone at Stripe.`,
        );
        setArmed(null);
        // Re-read rather than splice the row out: Stripe is the source of
        // truth here, and a list that disagrees with it is worse than a
        // second round-trip.
        await load(includeFlow);
      } finally {
        setDeleting(null);
      }
    },
    [includeFlow, load],
  );

  return (
    <section className="discount-codes-panel">
      <div className="discount-codes-head">
        <span className="discount-codes-title">
          Discount codes{rows !== null ? ` (${rows.length})` : ""}
        </span>
      </div>
      <div className="discount-codes-body">
          <div className="discount-codes-toolbar">
            <label className="discount-codes-toggle">
              <input
                type="checkbox"
                checked={includeFlow}
                onChange={(e) => setIncludeFlow(e.target.checked)}
              />{" "}
              Include welcome-flow codes
            </label>
            <span className="discount-codes-note">
              Live from Stripe — not a database table, so there is no{" "}
              <code>coupons</code> tab.
            </span>
          </div>
          {armed !== null ? (
            <p className="discount-codes-warning">
              Deleting stops anyone new redeeming <code>{armed}</code>. It does
              not reverse redemptions already made, and a subscription already
              carrying the discount keeps it. There is no undo.
            </p>
          ) : null}
          {notice !== null ? (
            <p className="discount-codes-notice">{notice}</p>
          ) : null}
          {error !== null ? (
            <p className="cal-action-error">{error}</p>
          ) : rows === null ? (
            <p className="empty">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="empty">No discount codes.</p>
          ) : (
            <table className="discount-codes-table table-cards">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Discount</th>
                  <th>Applies to</th>
                  <th>Uses</th>
                  <th>Who</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.coupon_id} className={c.valid ? "" : "is-spent"}>
                    <td className="table-card-title">
                      <code>{c.coupon_id}</code>
                    </td>
                    <td data-label="Name">{c.name ?? "—"}</td>
                    <td data-label="Discount">{discountText(c)}</td>
                    <td data-label="Applies to" title={railTitle(c, slugNames)}>
                      {railText(c, slugNames)}
                    </td>
                    <td data-label="Uses">{usesText(c)}</td>
                    <td data-label="Who">
                      {c.member_id === null ? (
                        "Anyone"
                      ) : (
                        <Link to={`/members/${c.member_id}`}>One member ↗</Link>
                      )}
                    </td>
                    <td data-label="Status">{c.valid ? "Live" : "Expired / used up"}</td>
                    <td className="discount-codes-actions">
                      {deleting === c.coupon_id ? (
                        <span className="discount-codes-busy">Deleting…</span>
                      ) : armed === c.coupon_id ? (
                        <>
                          <button
                            type="button"
                            className="discount-codes-delete-confirm"
                            disabled={deleting !== null}
                            onClick={() => void remove(c.coupon_id)}
                          >
                            Delete for good
                          </button>
                          <button
                            type="button"
                            className="discount-codes-keep"
                            disabled={deleting !== null}
                            onClick={() => setArmed(null)}
                          >
                            Keep
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="discount-codes-delete"
                          disabled={deleting !== null}
                          title="Retire this code at Stripe — no new redemptions"
                          onClick={() => {
                            setNotice(null);
                            setArmed(c.coupon_id);
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </section>
  );
}
